import React from 'react';

export interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

export const HELP_PAGE_TITLE = 'Help';

export const HELP_PAGE_DESCRIPTION =
  'Helpful guidance, common troubleshooting, and an easy way to request support.';

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'last-updated',
    question: 'How can I tell when the data on a page was last updated?',
    answer: (
      <>
        Look for the <strong>Last updated</strong> pill directly below the heading on each page. It
        shows the date the latest data displayed on that page was pulled, so you can quickly confirm
        how current the information is before starting a review or making a decision.
      </>
    ),
  },
  {
    id: 'changes-take-effect',
    question: 'When do approved promotion and demotion changes take effect?',
    answer: (
      <>
        Changes made during a given month remain in a <strong>staging state</strong> for the rest of
        that month. Approved promotional changes are written back to the database and take effect on
        the <strong>first working day of the following month</strong>. Fiscal demotion changes follow the
        annual review cycle and take effect on <strong>July 1</strong>, at the start of the new
        fiscal year.
      </>
    ),
  },
  {
    id: 'report-an-error',
    question: 'What should I do if I encounter an error or the data looks incorrect?',
    answer: (
      <>
        Go to the <strong>Support</strong> tab on the Help page and submit an ICM support request
        using the <strong>Plumeria ICM request</strong> link. Include the affected page, partner name
        or identifier, a description of the issue, and any screenshots or error details that can help
        the support team investigate.
      </>
    ),
  },
  {
    id: 'changes-requiring-action',
    question: 'How do I know which partner changes still require action?',
    answer: (
      <>
        Use the <strong>Monthly reviews</strong> page for promotional decisions and the{' '}
        <strong>Fiscal end review</strong> page for annual demotion decisions. Click the summary
        boxes at the top of the page to filter the list—they act like quick filters, letting you
        see how many partners are in total for review and how many have been approved, denied, and
        so on. Then open the partner record to review its current tier, proposed tier, revenue,
        contract status, and notes before approving or denying the change.
      </>
    ),
  },
  {
    id: 'confirm-decisions',
    question: 'Where can I confirm what changed and who made the decision?',
    answer: (
      <>
        Open <strong>Change history</strong> to review the audit trail for promotions, demotions,
        approvals, denials, and related partner updates. You can filter by change type, status, tier,
        date range, partner name, or identifier to find a specific record and confirm when the action
        occurred.
      </>
    ),
  },
  {
    id: 'export-review-data',
    question: 'Can I export review data for reconciliation or sharing?',
    answer: (
      <>
        Yes. Use the <strong>Export to CSV</strong> action on the relevant operational page to
        download the current data for reconciliation, offline analysis, or stakeholder review. Apply
        any needed filters before exporting so the file reflects the records you intend to share.
      </>
    ),
  },
];

export const SUPPORT_HEADING = 'Need assistance? Ask away!';

export const SUPPORT_BODY =
  'If you have any questions or need support, please submit an ICM through the ICM template linked below.';

export const SUPPORT_LINK_TEXT = 'Plumeria ICM request.';

export const SUPPORT_LINK_URL =
  'https://portal.microsofticm.com/imp/v3/incidents/create?tmpl=jy31KQ';
